// FpsCameraController.ts
import * as THREE from 'three';

import {
  MouseButtonType,
  MouseInputData,
  TouchInputData,
} from '../../utilities/input-event/input-event-data-extractor';
import { SkyviewFpsCamera } from '../skyview-fps-camera';
import { CameraControllerInterface, CameraControllerStateType } from '../types';

export class FpsCameraController implements CameraControllerInterface {
  state: CameraControllerStateType = CameraControllerStateType.Idle;

  private _yaw = 0; // around +Y               (radians)
  private _pitch = 0; // around camera local +X  (radians)
  readonly maxPitch = THREE.MathUtils.degToRad(89);
  readonly minPitch = -THREE.MathUtils.degToRad(89);
  readonly _sensitivity = 1;

  get yaw() {
    return this._yaw;
  }
  get pitch() {
    return this._pitch;
  }
  get sensitivity() {
    return this._sensitivity;
  }

  constructor(private camera: SkyviewFpsCamera) {
    // Derive initial yaw / pitch from the camera’s quaternion
    const euler = new THREE.Euler().setFromQuaternion(
      this.camera.getTHREECamera().quaternion,
      'YXZ',
      /* order  */ false,
    );
    this._yaw = euler.y;
    this._pitch = THREE.MathUtils.clamp(euler.x, this.minPitch, this.maxPitch);
  }

  onTouchStart(input: TouchInputData) {
    return false;
  }
  onTouchEnd(input: TouchInputData) {
    this.endInteraction();
    return false;
  }

  onTouchMove(input: TouchInputData) {
    if (input.touches.length === 1) {
      const d = input.center!.delta;
      this.applyRotation(-d.x, d.y);
      return true;
    }
    if (input.touches.length === 2 && input.touchLength) {
      // Pinch → zoom
      this.camera.zooming(-input.touchLength.delta * 1000);
      return true;
    }
    return false;
  }

  onMouseDown(input: MouseInputData) {
    if (input.button.type === MouseButtonType.RightButton) {
      this.state = CameraControllerStateType.Rotating;
      return true;
    }
    return false;
  }
  onMouseUp(_: MouseInputData) {
    this.endInteraction();
    return false;
  }

  onMouseMove(input: MouseInputData) {
    if (this.state === CameraControllerStateType.Rotating) {
      const d = input.pointer.delta;
      this.applyRotation(-d.x, d.y);
      return true;
    }
    return false;
  }

  onMouseWheel(input: MouseInputData) {
    this.camera.zooming(-input.wheel.delta);
    return true;
  }

  focusOnBox() {
    /* intentionally empty – FPS view doesn’t orbit */
  }
  focusOnAsset() {
    /** */
  }
  getFocusedAsset() {
    return undefined;
  }
  focusOnScene() {
    /** */
  }
  isTransitioning() {
    return this.state !== CameraControllerStateType.Idle;
  }

  applyRotation(deltaX: number, deltaY: number) {
    const zoomCompensation = this.camera.getZoomAmountPercent();
    this._yaw += deltaX * this.sensitivity * Math.max(0.3, 1.0 - zoomCompensation);
    this._pitch += deltaY * this.sensitivity * Math.max(0.3, 1.0 - zoomCompensation);
    this._pitch = THREE.MathUtils.clamp(this.pitch, this.minPitch, this.maxPitch);

    const quat = new THREE.Quaternion().setFromEuler(
      new THREE.Euler(this._pitch, this._yaw, 0, 'YXZ'),
    );
    const threeCam = this.camera.getTHREECamera();
    threeCam.quaternion.copy(quat);
    threeCam.updateMatrixWorld();
  }

  endInteraction() {
    this.state = CameraControllerStateType.Idle;
  }

  /* -- Unused interface stubs (compile-time satisfy only) ---------------- */
  onMouseMoveCapture?(): boolean {
    return false;
  }
  onMouseDownCapture?(): boolean {
    return false;
  }
  onMouseUpCapture?(): boolean {
    return false;
  }
  onMouseWheelCapture?(): boolean {
    return false;
  }
  onTouchStartCapture?(): boolean {
    return false;
  }
  onTouchMoveCapture?(): boolean {
    return false;
  }
  onTouchEndCapture?(): boolean {
    return false;
  }
}
